import React from 'react';

// Base skeleton block with shimmer animation
export const SkeletonBlock = ({ className = '', style }) => (
    <div
        className={`animate-shimmer rounded ${className}`}
        style={style}
    />
);

// Text line skeleton
export const SkeletonText = ({ width = '100%', height = '1rem', className = '' }) => (
    <SkeletonBlock className={className} style={{ width, height }} />
);

// Circle skeleton (avatars, icons)
export const SkeletonCircle = ({ size = '3rem', className = '' }) => (
    <SkeletonBlock
        className={`rounded-full ${className}`}
        style={{ width: size, height: size }}
    />
);

// Product card skeleton
export const ProductCardSkeleton = () => (
    <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden shadow-soft">
        {/* Image */}
        <div className="aspect-square bg-gray-100 p-4">
            <SkeletonBlock className="w-full h-full rounded-lg" />
        </div>
        {/* Content */}
        <div className="p-3 space-y-2">
            <SkeletonText width="60%" height="0.75rem" />
            <SkeletonText width="90%" height="0.875rem" />
            <div className="flex items-center gap-2 pt-1">
                <SkeletonText width="40%" height="1.125rem" />
                <SkeletonText width="25%" height="0.75rem" />
            </div>
            <div className="flex items-center gap-1">
                <SkeletonText width="5rem" height="0.75rem" />
            </div>
            <SkeletonBlock className="w-full mt-2" style={{ height: '2.25rem' }} />
        </div>
    </div>
);

// Product detail page skeleton
export const ProductDetailSkeleton = () => (
    <div className="min-h-screen bg-white">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 py-4 sm:py-6">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 mb-6">
                <SkeletonText width="4rem" height="0.75rem" />
                <SkeletonText width="1rem" height="0.75rem" />
                <SkeletonText width="8rem" height="0.75rem" />
                <SkeletonText width="1rem" height="0.75rem" />
                <SkeletonText width="6rem" height="0.75rem" />
            </div>

            {/* Title */}
            <div className="mb-6">
                <SkeletonText width="12rem" height="1.75rem" className="mb-2" />
                <SkeletonText width="20rem" height="1.5rem" />
            </div>

            {/* Main Content - Image + Details */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                {/* Left - Images */}
                <div className="flex gap-4">
                    {/* Thumbnails */}
                    <div className="flex flex-col gap-2">
                        {[...Array(3)].map((_, i) => (
                            <SkeletonBlock key={i} className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg" />
                        ))}
                    </div>
                    {/* Main Image */}
                    <SkeletonBlock className="flex-1 rounded-lg" style={{ minHeight: '400px' }} />
                </div>

                {/* Right - Product Info */}
                <div className="space-y-4">
                    <div>
                        <SkeletonText width="10rem" height="1.5rem" className="mb-2" />
                        <SkeletonText width="16rem" height="1.25rem" className="mb-3" />
                    </div>
                    {/* Delivery info */}
                    <div className="flex items-center gap-2">
                        <SkeletonCircle size="1.25rem" />
                        <SkeletonText width="14rem" height="0.875rem" />
                    </div>
                    {/* Badges */}
                    <div className="flex gap-2">
                        <SkeletonBlock className="rounded-full" style={{ width: '8rem', height: '1.75rem' }} />
                        <SkeletonBlock className="rounded-full" style={{ width: '7rem', height: '1.75rem' }} />
                    </div>
                    {/* Price */}
                    <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                        <SkeletonText width="8rem" height="2rem" />
                        <div className="flex items-center gap-3">
                            <SkeletonText width="5rem" height="1rem" />
                            <SkeletonBlock className="rounded-full" style={{ width: '4rem', height: '1.5rem' }} />
                        </div>
                    </div>
                    {/* Part number, SKU */}
                    <div className="space-y-2">
                        <SkeletonText width="12rem" height="0.875rem" />
                        <SkeletonText width="10rem" height="0.875rem" />
                    </div>
                    {/* Quantity + Buttons */}
                    <div className="flex items-center gap-3 pt-2">
                        <SkeletonBlock style={{ width: '7rem', height: '2.5rem' }} className="rounded-lg" />
                    </div>
                    <div className="flex gap-3 pt-2">
                        <SkeletonBlock className="flex-1 rounded-lg" style={{ height: '3rem' }} />
                        <SkeletonBlock className="flex-1 rounded-lg" style={{ height: '3rem' }} />
                    </div>
                    <SkeletonBlock className="w-full rounded-lg" style={{ height: '3rem' }} />
                </div>
            </div>

            {/* Product Attributes Table */}
            <div className="bg-gray-50 rounded-lg p-4 mb-8">
                <SkeletonText width="12rem" height="1.25rem" className="mb-4" />
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {[...Array(6)].map((_, i) => (
                        <div key={i} className="bg-white rounded-lg p-3 space-y-2">
                            <SkeletonText width="60%" height="0.75rem" />
                            <SkeletonText width="80%" height="0.875rem" />
                        </div>
                    ))}
                </div>
            </div>

            {/* Recommendations skeleton */}
            <div className="mb-8">
                <SkeletonText width="14rem" height="1.25rem" className="mb-4" />
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                    {[...Array(4)].map((_, i) => (
                        <ProductCardSkeleton key={i} />
                    ))}
                </div>
            </div>
        </div>
    </div>
);

// Cart item skeleton
export const CartItemSkeleton = () => (
    <div className="bg-white rounded-xl border border-gray-200 p-4 flex gap-4">
        {/* Image */}
        <SkeletonBlock className="rounded-lg flex-shrink-0" style={{ width: '6rem', height: '6rem' }} />
        {/* Details */}
        <div className="flex-1 space-y-2">
            <SkeletonText width="70%" height="1rem" />
            <SkeletonText width="40%" height="0.75rem" />
            <div className="flex items-center gap-3 pt-1">
                <SkeletonText width="5rem" height="1.25rem" />
                <SkeletonText width="3rem" height="0.75rem" />
            </div>
            <div className="flex items-center justify-between pt-2">
                {/* Quantity controls */}
                <SkeletonBlock className="rounded-lg" style={{ width: '7rem', height: '2rem' }} />
                {/* Remove button */}
                <SkeletonBlock className="rounded-lg" style={{ width: '2rem', height: '2rem' }} />
            </div>
        </div>
    </div>
);

// Cart page skeleton
export const CartSkeleton = () => (
    <div className="py-4">
        <div className="flex items-center justify-between mb-4">
            <SkeletonText width="10rem" height="1.5rem" />
            <SkeletonText width="6rem" height="0.875rem" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Items column */}
            <div className="lg:col-span-2 space-y-3">
                {[...Array(3)].map((_, i) => (
                    <CartItemSkeleton key={i} />
                ))}
            </div>
            {/* Order summary */}
            <div className="bg-white rounded-xl border border-gray-200 p-5 h-fit space-y-3">
                <SkeletonText width="8rem" height="1.125rem" className="mb-4" />
                {[...Array(4)].map((_, i) => (
                    <div key={i} className="flex justify-between">
                        <SkeletonText width="6rem" height="0.875rem" />
                        <SkeletonText width="4rem" height="0.875rem" />
                    </div>
                ))}
                <div className="border-t border-gray-200 pt-3 mt-3">
                    <div className="flex justify-between">
                        <SkeletonText width="4rem" height="1.125rem" />
                        <SkeletonText width="5rem" height="1.125rem" />
                    </div>
                </div>
                <SkeletonBlock className="w-full rounded-lg mt-4" style={{ height: '3rem' }} />
            </div>
        </div>
    </div>
);

// Vehicle card skeleton for SearchSection
export const VehicleCardSkeleton = () => (
    <div className="bg-white rounded-2xl border border-gray-100 p-4 space-y-3 shadow-soft">
        <div className="flex items-center gap-3">
            <SkeletonBlock className="rounded-lg" style={{ width: '5rem', height: '3.5rem' }} />
            <div className="flex-1 space-y-2">
                <SkeletonText width="70%" height="1rem" />
                <SkeletonText width="50%" height="0.75rem" />
            </div>
        </div>
        <div className="flex gap-2">
            <SkeletonBlock className="flex-1 rounded-lg" style={{ height: '2rem' }} />
            <SkeletonBlock className="flex-1 rounded-lg" style={{ height: '2rem' }} />
        </div>
    </div>
);

// Recommendation grid skeleton
export const RecommendationSkeleton = ({ count = 4, title = '' }) => (
    <div className="py-6 sm:py-8">
        {title ? (
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6 px-4 sm:px-0">{title}</h2>
        ) : (
            <SkeletonText width="14rem" height="1.5rem" className="mb-4 sm:mb-6 mx-4 sm:mx-0" />
        )}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 sm:gap-4 px-4 sm:px-0">
            {[...Array(count)].map((_, i) => (
                <ProductCardSkeleton key={i} />
            ))}
        </div>
    </div>
);

// Banner / Hero skeleton (BoodmoUi)
export const BannerSkeleton = () => (
    <section className="relative bg-transparent">
        <div className="relative flex h-[132px] sm:h-[156px] md:h-[184px] w-full overflow-hidden bg-transparent">
            <div className="flex min-w-0 flex-1 flex-col justify-center gap-2 py-2.5 pl-3 pr-2 sm:py-3 sm:pl-4 md:pl-6">
                <div className="flex overflow-hidden rounded-xl border border-slate-700/70 bg-slate-900/90 shadow-sm sm:rounded-2xl">
                    <div className="flex flex-1 flex-col justify-center gap-2 px-3 py-2.5">
                        <SkeletonText width="45%" height="0.5rem" />
                        <SkeletonText width="92%" height="0.75rem" />
                    </div>
                    <SkeletonBlock className="w-14 shrink-0 rounded-none bg-slate-700 sm:w-16" />
                </div>
                <SkeletonBlock className="h-8 w-full rounded-lg bg-slate-800 sm:h-9 sm:rounded-xl" />
            </div>
            <SkeletonBlock className="h-full min-h-[6rem] w-[34%] shrink-0 min-w-[6.5rem] self-stretch rounded-none opacity-90 sm:w-[38%] md:max-w-[min(42vw,17rem)]" />
        </div>
        <div className="border-t border-slate-800/80 bg-sky-900 py-2 sm:py-2.5 px-4">
            <SkeletonText width="85%" height="0.75rem" className="mx-auto max-w-3xl opacity-60" />
        </div>
    </section>
);

// Category card skeleton (SearchByCategory)
export const CategoryCardSkeleton = () => (
    <div className="flex flex-col items-center justify-center p-4 sm:p-5 md:p-6 rounded-2xl bg-white shadow-soft w-full border border-gray-100">
        <SkeletonBlock className="w-24 h-24 sm:w-28 sm:h-28 md:w-32 md:h-32 lg:w-36 lg:h-36 mb-3 rounded-lg" />
        <SkeletonText width="70%" height="0.75rem" />
    </div>
);

// Category section skeleton (SearchByCategory grid)
export const CategorySectionSkeleton = () => (
    <div className="py-6 sm:py-8 px-3 sm:px-4 md:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-4">
            <SkeletonText width="10rem" height="1.5rem" />
            <SkeletonText width="5rem" height="1rem" />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 sm:gap-4 md:gap-5">
            {[...Array(6)].map((_, i) => (
                <CategoryCardSkeleton key={i} />
            ))}
        </div>
    </div>
);

// Workshop / Mechanic card skeleton
export const WorkshopCardSkeleton = ({ lines = 2 }) => (
    <div className="relative h-[120px] sm:h-[140px] md:h-[200px] lg:h-[250px] rounded-2xl overflow-hidden bg-gray-100 shadow-soft">
        <SkeletonBlock className="w-full h-full rounded-none" />
        <div className="absolute bottom-0 left-0 right-0 p-2 md:p-3 flex flex-col gap-1">
            <SkeletonText width="70%" height="0.75rem" />
            <SkeletonText width="50%" height="0.625rem" />
            {lines >= 3 && <SkeletonText width="60%" height="0.5rem" />}
        </div>
    </div>
);

// Workshop section skeleton (2x2 mobile grid, 2 desktop cards + map placeholder)
export const WorkshopSectionSkeleton = ({ title = '', lines = 2 }) => (
    <div className="relative bg-white py-4 sm:py-6 md:py-8 overflow-hidden">
        <div className="px-3 sm:px-4 md:px-6 lg:px-8 mb-3 sm:mb-4">
            <SkeletonText width="12rem" height="1.5rem" />
        </div>
        <div className="flex flex-col lg:flex-row gap-4 px-3 sm:px-4 md:px-6 lg:px-8">
            <div className="w-full lg:w-2/3">
                <div className="grid grid-cols-2 gap-3 lg:hidden">
                    {[...Array(4)].map((_, i) => (
                        <WorkshopCardSkeleton key={i} lines={lines} />
                    ))}
                </div>
                <div className="hidden lg:grid lg:grid-cols-2 gap-4">
                    {[...Array(2)].map((_, i) => (
                        <WorkshopCardSkeleton key={i} lines={lines} />
                    ))}
                </div>
            </div>
            <div className="w-full lg:w-1/3 h-[200px] lg:h-auto">
                <SkeletonBlock className="w-full h-full rounded-lg" style={{ minHeight: '200px' }} />
            </div>
        </div>
    </div>
);

// Vehicle cards section skeleton (SearchSection)
export const VehicleSectionSkeleton = () => (
    <section className="relative bg-gradient-to-br from-gray-50 via-white to-gray-50 pt-12 pb-3 sm:pt-16 md:pt-16 sm:pb-6 md:pb-8">
        <div className="w-full mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
            <div className="flex flex-wrap justify-center items-start gap-2 sm:gap-3 md:gap-4 lg:gap-5 mb-3 sm:mb-4 md:mb-6 lg:mb-8">
                {[...Array(3)].map((_, i) => (
                    <div key={i} className="w-[calc((100%-2*0.5rem)/3)] sm:w-[calc((100%-1*0.75rem)/2)] md:w-[240px] lg:w-[220px] xl:w-[200px]">
                        <VehicleCardSkeleton />
                    </div>
                ))}
            </div>
        </div>
    </section>
);

export default {
    SkeletonBlock,
    SkeletonText,
    SkeletonCircle,
    ProductCardSkeleton,
    ProductDetailSkeleton,
    CartItemSkeleton,
    CartSkeleton,
    VehicleCardSkeleton,
    RecommendationSkeleton,
    BannerSkeleton,
    CategoryCardSkeleton,
    CategorySectionSkeleton,
    WorkshopCardSkeleton,
    WorkshopSectionSkeleton,
    VehicleSectionSkeleton,
};
